import { IMAGE_EXTENSIONS, isImageFile } from "./imagePrep";

export const validateFileName = (filename) => {
    const dateMatch = filename.match(/(\d{4})(\d{2})(\d{2})/);
    if (!dateMatch) {
        return { valid: false, error: "Filename must contain a test date in YYYYMMDD format." };
    }

    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10);
    const day = parseInt(dateMatch[3], 10);
    const testDate = new Date(year, month - 1, day);
    const today = new Date();
    const minDate = new Date(1900, 0, 1);

    if (testDate > today || testDate < minDate || month < 1 || month > 12 || day < 1 || day > 31) {
        return { valid: false, error: "Date must be a valid date before today." };
    }

    return { valid: true };
};

export const validateFileType = (file) => {
    const allowedTypes = ["pdf", "csv", "json", ...IMAGE_EXTENSIONS];
    const fileType = file.name.split(".").pop().toLowerCase();

    if (!allowedTypes.includes(fileType)) {
        return { valid: false, error: "Unsupported file type. Please select a PDF, CSV, JSON, or image file." };
    }
    return { valid: true, fileType };
};

export const validateFileSize = (file, maxSizeMB = 10) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
        return { valid: false, error: `File size exceeds ${maxSizeMB}MB limit.` };
    }
    return { valid: true };
};

export const validateCsvContent = async (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split("\n");
            if (lines.length < 2) {
                return resolve({ valid: false, error: "CSV file appears to be empty or invalid." });
            }

            const requiredHeaders = ["test_name", "result", "lab_low", "lab_high", "units"];
            const headers = lines[0].split(",").map(h => h.trim().toLowerCase());

            for (const header of requiredHeaders) {
                if (!headers.includes(header)) {
                    return resolve({ valid: false, error: `Missing required column: ${header}` });
                }
            }

            for (let i = 1; i < lines.length; i++) {
                const columns = lines[i].split(",");
                if (columns.length < requiredHeaders.length) {
                    return resolve({ valid: false, error: `Invalid row format at line ${i + 1}` });
                }
            }

            resolve({ valid: true });
        };
        reader.readAsText(file);
    });
};

export const validateJsonContent = async (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const jsonData = JSON.parse(e.target.result);

                if (!jsonData.header || jsonData.header.type !== "bloodtest") {
                    return resolve({
                        valid: false,
                        error: "Invalid JSON structure: 'header.type' must be 'bloodtest'."
                    });
                }

                if (!jsonData.body || !Array.isArray(jsonData.body)) {
                    return resolve({ valid: false, error: "Invalid JSON structure: 'body' must be an array." });
                }

                for (const entry of jsonData.body) {
                    const requiredKeys = ["test_name", "result", "units", "lab_low", "lab_high"];

                    for (const key of requiredKeys) {
                        if (!Object.prototype.hasOwnProperty.call(entry, key)) {
                            console.log(entry);
                            return resolve({ 
                                valid: false, 
                                error: `Each entry in 'body' must have '${key}'.` 
                            });
                        }
                    }
                }

                resolve({ valid: true });
            } catch (error) {
                resolve({ valid: false, error: "Invalid JSON format." });
            }
        };
        reader.readAsText(file);
    });
};

export const validateFile = async (file) => {
    const typeValidation = validateFileType(file);
    if (!typeValidation.valid) return typeValidation;

    // Images are shrunk in the browser before upload (imagePrep), so a large
    // camera original is fine; what reaches the Lambda is a few hundred KB.
    const sizeValidation = validateFileSize(file, isImageFile(file) ? 25 : 10);
    if (!sizeValidation.valid) return sizeValidation;

    // Only CSV uploads need a date in the filename — it's the sole source of the
    // test's `testdate` (see convertCsvToJson in UploadUtil). PDFs and JSON carry
    // their own date in the file content, so their filenames aren't validated.
    if (typeValidation.fileType === "csv") {
        const nameValidation = validateFileName(file.name);
        if (!nameValidation.valid) return nameValidation;
        return await validateCsvContent(file);
    }

    if (typeValidation.fileType === "json") {
        return await validateJsonContent(file);
    }

    return { valid: true };
};

export const validateNewTestInputs = (testData) => {
    if (!testData?.header?.first_name || !testData?.header?.last_name || !testData?.header?.birthdate) {
        return false;
    }

    if (!Array.isArray(testData.body) || testData.body.length === 0) {
        return false;
    }

    for (let i = 0; i < testData.body.length; i++) {
        const { test_name, result } = testData.body[i];
        if (!test_name || !result) {
            return false;
        }
    }

    return true;
};

export const getNewTestWarnings = (testData) => {
    const warnings = [];
    if (!Array.isArray(testData?.body)) return warnings;

    for (const entry of testData.body) {
        if (!entry.units) warnings.push(`"${entry.test_name}" is missing units`);
        if (!entry.lab_low && entry.lab_low !== 0) warnings.push(`"${entry.test_name}" is missing lab low`);
        if (!entry.lab_high && entry.lab_high !== 0) warnings.push(`"${entry.test_name}" is missing lab high`);
    }

    return warnings;
};